import React, { useState, useEffect } from 'react';
import { Package, DollarSign, Calendar, CheckCircle, Plus, Edit, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';

const ServicePackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    billing_frequency: 'monthly',
    services_included: ['']
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await api.get('/service-packages');
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load service packages');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        description: pkg.description,
        price: pkg.price,
        billing_frequency: pkg.billing_frequency,
        services_included: pkg.services_included
      });
    } else {
      setEditingPackage(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        billing_frequency: 'monthly',
        services_included: ['']
      });
    }
    setDialogOpen(true);
  };

  const handleServiceChange = (index, value) => {
    const newServices = [...formData.services_included];
    newServices[index] = value;
    setFormData({ ...formData, services_included: newServices });
  };

  const handleAddService = () => {
    setFormData({
      ...formData,
      services_included: [...formData.services_included, '']
    });
  };

  const handleRemoveService = (index) => {
    const newServices = formData.services_included.filter((_, i) => i !== index);
    setFormData({ ...formData, services_included: newServices });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.name || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    const filteredServices = formData.services_included.filter(s => s.trim() !== '');
    if (filteredServices.length === 0) {
      toast.error('Please add at least one service');
      return;
    }

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        services_included: filteredServices
      };

      if (editingPackage) {
        await api.put(`/service-packages/${editingPackage.id}`, payload);
        toast.success('Service package updated successfully');
      } else {
        await api.post('/service-packages', payload);
        toast.success('Service package created successfully');
      }

      setDialogOpen(false);
      fetchPackages();
    } catch (error) {
      console.error('Failed to save package:', error);
      toast.error('Failed to save service package');
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this service package?')) {
      return;
    }

    try {
      await api.delete(`/service-packages/${packageId}`);
      toast.success('Service package deleted successfully');
      fetchPackages();
    } catch (error) {
      console.error('Failed to delete package:', error);
      toast.error('Failed to delete service package');
    }
  };

  const getBillingLabel = (frequency) => {
    const labels = {
      one_time: 'One-time',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      annual: 'Annual'
    };
    return labels[frequency] || frequency;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="packages-title">Service Packages</h2>
          <p className="text-gray-600">Manage accounting services and packages</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Service Package
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg, index) => (
          <div
            key={pkg.id}
            className="glass rounded-xl p-6 hover-lift transition-all"
            data-testid={`package-card-${index}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-teal-600" />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleOpenDialog(pkg)}
                  className="h-8 w-8 p-0"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(pkg.id)}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
            <p className="text-gray-600 mb-4 text-sm">{pkg.description}</p>
            
            <div className="flex items-baseline mb-4">
              <span className="text-3xl font-bold text-teal-600">${pkg.price}</span>
              <span className="text-gray-500 ml-2">/ {getBillingLabel(pkg.billing_frequency)}</span>
            </div>

            <div className="space-y-2 mb-4">
              <p className="text-sm font-medium text-gray-700">Includes:</p>
              {pkg.services_included.map((service, idx) => (
                <div key={idx} className="flex items-start text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 mr-2 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>{service}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPackage ? 'Edit Service Package' : 'Add Service Package'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Package Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Basic Bookkeeping"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the package..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Price *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <Label htmlFor="billing_frequency">Billing Frequency *</Label>
                <Select
                  value={formData.billing_frequency}
                  onValueChange={(value) => setFormData({ ...formData, billing_frequency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-time</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Services Included *</Label>
              <div className="space-y-2 mt-2">
                {formData.services_included.map((service, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={service}
                      onChange={(e) => handleServiceChange(index, e.target.value)}
                      placeholder="e.g., Monthly financial statements"
                    />
                    {formData.services_included.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveService(index)}
                        className="text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddService}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Service
                </Button>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
              >
                {editingPackage ? 'Update Package' : 'Create Package'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicePackages;
